// ============================================================================
// PROJECT GRAPH TAB
// Full-page knowledge graph view with actions for syncing session data,
// clearing graph, and rebuilding research data into Neo4j.
// ============================================================================

import React, { useState } from 'react';
import { Database, Loader2, CheckCircle, AlertTriangle, Trash2, Lightbulb, FileText, Shield, Camera, FolderRoot, Scale, Hammer, Bot } from 'lucide-react';
import { customerResearchService } from '../../services/sessionTasksService';
import { CustomerResearchGraph } from './CustomerResearchGraph';

interface ProjectGraphTabProps {
  projectId: string;
  token: string;
}

type ActionResult = {
  type: 'success' | 'error';
  message: string;
  stats?: Record<string, number>;
  errors?: string[];
};

interface BackfillButtonConfig {
  key: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  loading: boolean;
  onClick: () => void;
  primary?: boolean;
}

const BackfillButton: React.FC<{ config: BackfillButtonConfig; disabled: boolean }> = ({ config, disabled }) => {
  const Icon = config.icon;
  const className = config.primary
    ? 'flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-white shadow-sm shadow-primary/20 hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
    : 'flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  return (
    <button onClick={config.onClick} disabled={disabled} className={className}>
      {config.loading ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />}
      {config.label}
    </button>
  );
};

const ClearGraphControl: React.FC<{
  confirmClear: boolean;
  clearing: boolean;
  disabled: boolean;
  onClear: () => void;
  onCancel: () => void;
}> = ({ confirmClear, clearing, disabled, onClear, onCancel }) => {
  if (confirmClear) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-2">
        <span className="text-xs text-red-700 font-medium">Delete all graph data?</span>
        <button
          onClick={onClear}
          disabled={disabled}
          className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 transition-colors disabled:opacity-50"
        >
          Confirm
        </button>
        <button
          onClick={onCancel}
          className="rounded-lg bg-white border border-[#e2e8f0] px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={onClear}
      disabled={disabled}
      className="flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {clearing ? (
        <>
          <Loader2 size={16} className="animate-spin" />
          Clearing...
        </>
      ) : (
        <>
          <Trash2 size={16} />
          Clear Graph
        </>
      )}
    </button>
  );
};

const ActionResultBanner: React.FC<{ result: ActionResult }> = ({ result }) => (
  <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${
    result.type === 'success'
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-red-50 text-red-700 border-red-200'
  }`}>
    {result.type === 'success' ? <CheckCircle size={18} className="mt-0.5 shrink-0" /> : <AlertTriangle size={18} className="mt-0.5 shrink-0" />}
    <div className="flex-1">
      <p className="font-medium">{result.message}</p>
      {result.stats && (
        <div className="flex flex-wrap gap-4 mt-2 text-xs">
          {Object.entries(result.stats).map(([key, val]) => (
            <span key={key}><strong>{val}</strong> {key.replace(/([A-Z])/g, ' $1').toLowerCase().trim()}</span>
          ))}
        </div>
      )}
      {result.errors && result.errors.length > 0 && (
        <details className="mt-2">
          <summary className="text-xs cursor-pointer font-medium flex items-center gap-1">
            <AlertTriangle size={12} /> {result.errors.length} warning{result.errors.length !== 1 ? 's' : ''}
          </summary>
          <ul className="mt-1 text-xs space-y-0.5 pl-4 list-disc text-amber-700">
            {result.errors.slice(0, 10).map((e) => <li key={e}>{e}</li>)}
            {result.errors.length > 10 && <li>...and {result.errors.length - 10} more</li>}
          </ul>
        </details>
      )}
    </div>
  </div>
);

export const ProjectGraphTab: React.FC<ProjectGraphTabProps> = ({
  projectId,
}) => {
  const [backfilling, setBackfilling] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [backfillingIdeation, setBackfillingIdeation] = useState(false);
  const [backfillingTaskOutputs, setBackfillingTaskOutputs] = useState(false);
  const [backfillingGovernance, setBackfillingGovernance] = useState(false);
  const [backfillingProject, setBackfillingProject] = useState(false);
  const [backfillingDeviceImages, setBackfillingDeviceImages] = useState(false);
  const [backfillingDecisions, setBackfillingDecisions] = useState(false);
  const [backfillingBuilds, setBackfillingBuilds] = useState(false);
  const [backfillingAvatars, setBackfillingAvatars] = useState(false);
  const [backfillingProjectAvatars, setBackfillingProjectAvatars] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [actionResult, setActionResult] = useState<ActionResult | null>(null);
  const [graphKey, setGraphKey] = useState(0);

  const runBackfillAction = async (
    setLoading: (v: boolean) => void,
    action: () => Promise<any>,
    toResult: (r: any) => Omit<ActionResult, 'type'>,
    errorFallback: string,
  ) => {
    setLoading(true);
    setActionResult(null);
    try {
      const result = await action();
      setActionResult({ type: 'success', ...toResult(result) });
    } catch (err: any) {
      setActionResult({ type: 'error', message: err.response?.data?.error || err.message || errorFallback });
    } finally {
      setLoading(false);
    }
  };

  const handleBackfill = () => runBackfillAction(
    setBackfilling,
    () => customerResearchService.backfillSessionsToGraph(projectId),
    r => ({ message: r.message, stats: r.stats, errors: r.errors }),
    'Failed to start backfill',
  );

  const handleBackfillIdeation = () => runBackfillAction(
    setBackfillingIdeation,
    () => customerResearchService.backfillIdeation(projectId),
    r => ({ message: r.message, stats: r.stats, errors: r.errors }),
    'Failed to backfill ideation',
  );

  const handleBackfillTaskOutputs = () => runBackfillAction(
    setBackfillingTaskOutputs,
    () => customerResearchService.backfillTaskOutputs(projectId),
    r => ({ message: r.message, stats: r.stats, errors: r.errors }),
    'Failed to backfill task outputs',
  );

  const handleBackfillGovernance = () => runBackfillAction(
    setBackfillingGovernance,
    () => customerResearchService.backfillGovernance(projectId),
    r => ({ message: r.message, stats: r.stats, errors: r.errors }),
    'Failed to backfill governance',
  );

  const handleBackfillProject = () => runBackfillAction(
    setBackfillingProject,
    () => customerResearchService.backfillProject(projectId),
    r => ({ message: r.message, stats: r.stats }),
    'Failed to backfill project',
  );

  const handleBackfillDeviceImages = () => runBackfillAction(
    setBackfillingDeviceImages,
    () => customerResearchService.backfillDeviceImages(projectId),
    r => ({ message: r.message, stats: r.stats, errors: r.errors }),
    'Failed to backfill device images',
  );

  const handleBackfillDecisions = () => runBackfillAction(
    setBackfillingDecisions,
    () => customerResearchService.backfillDecisions(projectId),
    r => ({ message: r.message, stats: r.stats, errors: r.errors }),
    'Failed to backfill decisions',
  );

  const handleBackfillBuilds = () => runBackfillAction(
    setBackfillingBuilds,
    () => customerResearchService.backfillBuilds(projectId),
    r => ({ message: r.message, stats: r.stats, errors: r.errors }),
    'Failed to backfill builds',
  );

  const handleBackfillAvatars = () => runBackfillAction(
    setBackfillingAvatars,
    () => customerResearchService.backfillAvatars(projectId),
    r => ({ message: r.message, stats: r.stats, errors: r.errors }),
    'Failed to backfill avatars',
  );

  const handleBackfillProjectAvatars = () => runBackfillAction(
    setBackfillingProjectAvatars,
    () => customerResearchService.backfillProjectAvatars(projectId),
    r => ({ message: r.message, stats: { count: r.count }, errors: r.errors }),
    'Failed to sync project avatars',
  );

  const handleClear = async () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setClearing(true);
    setConfirmClear(false);
    setActionResult(null);
    try {
      const result = await customerResearchService.clearGraph(projectId);
      setActionResult({ type: 'success', message: result.message, stats: { deletedNodes: result.deletedNodes } });
      setGraphKey(k => k + 1);
    } catch (err: any) {
      setActionResult({ type: 'error', message: err.response?.data?.error || err.message || 'Failed to clear graph' });
    } finally {
      setClearing(false);
    }
  };

  const anyLoading = backfilling || clearing || backfillingIdeation || backfillingTaskOutputs || backfillingGovernance || backfillingProject || backfillingDeviceImages || backfillingDecisions || backfillingBuilds || backfillingAvatars || backfillingProjectAvatars;

  const backfillButtons: BackfillButtonConfig[] = [
    { key: 'sessions', label: 'Sessions', icon: Database, loading: backfilling, onClick: handleBackfill, primary: true },
    { key: 'ideation', label: 'Ideation', icon: Lightbulb, loading: backfillingIdeation, onClick: handleBackfillIdeation },
    { key: 'tasks', label: 'Tasks', icon: FileText, loading: backfillingTaskOutputs, onClick: handleBackfillTaskOutputs },
    { key: 'project', label: 'Project', icon: FolderRoot, loading: backfillingProject, onClick: handleBackfillProject },
    { key: 'governance', label: 'Governance', icon: Shield, loading: backfillingGovernance, onClick: handleBackfillGovernance },
    { key: 'device-images', label: 'Device Images', icon: Camera, loading: backfillingDeviceImages, onClick: handleBackfillDeviceImages },
    { key: 'decisions', label: 'Decisions', icon: Scale, loading: backfillingDecisions, onClick: handleBackfillDecisions },
    { key: 'builds', label: 'Builds', icon: Hammer, loading: backfillingBuilds, onClick: handleBackfillBuilds },
    { key: 'avatars', label: 'Avatars', icon: Bot, loading: backfillingAvatars, onClick: handleBackfillAvatars },
    { key: 'project-avatars', label: 'Project Avatars', icon: Bot, loading: backfillingProjectAvatars, onClick: handleBackfillProjectAvatars },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Action bar */}
      <div className="bg-white rounded-xl border border-[#e2e8f0] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-navy">Graph Management</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Sync session data, rebuild research entities, or clear the graph.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Clear Graph */}
            <ClearGraphControl
              confirmClear={confirmClear}
              clearing={clearing}
              disabled={anyLoading}
              onClear={handleClear}
              onCancel={() => setConfirmClear(false)}
            />
          </div>
        </div>

        {/* Backfill buttons row */}
        <div className="flex items-center gap-2 pt-2 border-t border-[#e2e8f0]">
          <span className="text-xs font-medium text-slate-400 mr-1">Backfill:</span>

          {backfillButtons.map((config) => (
            <BackfillButton key={config.key} config={config} disabled={anyLoading} />
          ))}
        </div>
      </div>

      {/* Action result */}
      {actionResult && <ActionResultBanner result={actionResult} />}

      {/* Graph visualization */}
      <section className="bg-white rounded-xl border border-[#e2e8f0]">
        <div className="p-5">
          <CustomerResearchGraph key={graphKey} projectId={projectId} allowMerge />
        </div>
      </section>
    </div>
  );
};

export default ProjectGraphTab;
